import { useEffect, useMemo, useState, useCallback } from 'react';
import { Search, Eye, Phone, MessageCircle, Navigation2, Sparkles, X, ExternalLink, Calendar, CheckSquare, Square, Trash, Check, Clock, Globe, MapPin, Video, Image as ImageIcon, Edit3, Upload, Tag, RefreshCw, Loader2 } from 'lucide-react';
import { Card, SectionHeader, Badge, Button, Empty } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { listingsApi, categoriesApi } from '@/lib/api';
import { timeAgo, slugify } from '@/lib/format';
import { Listing } from '@/lib/types';
import { INDIAN_CITIES, BUSINESS_CATEGORIES } from '@/lib/data_registry';
import { convertFileToWebP } from '@/lib/image_utils';

const statuses = ['all', 'published', 'pending_approval', 'review', 'draft'] as const;

function isSameDay(d1: Date, d2: Date) {
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
}

function getYesterday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
}

export default function Listings() {
  const [rows, setRows] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<(typeof statuses)[number]>('all');
  const [dbCategories, setDbCategories] = useState<string[]>([]);

  useEffect(() => {
    categoriesApi.getCategories().then(cats => {
      if (Array.isArray(cats)) {
        setDbCategories(cats.map(c => c.name).filter(Boolean));
      }
    }).catch(err => console.warn('Could not load categories:', err));
  }, []);
  
  // Sorting State
  const [sortBy, setSortBy] = useState<string>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Multi-select Checkboxes State
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  
  // Multi-Select Category Filter State
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [catSearchText, setCatSearchText] = useState('');
  const [showCategoryFilterPopover, setShowCategoryFilterPopover] = useState(false);

  // Multi-Select City Filter State
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [citySearchText, setCitySearchText] = useState('');
  const [showCityFilterPopover, setShowCityFilterPopover] = useState(false);

  // Elite Status Filter State
  const [eliteFilter, setEliteFilter] = useState<'all' | 'elite' | 'normal'>('all');

  // Date Filtering State
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'yesterday' | '7days' | 'this_month' | 'last_month' | 'custom'>('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Pagination State
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [open, setOpen] = useState<Listing | null>(null);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  const fetchRows = useCallback(async (searchQuery = q, statusFilter = status, sBy = sortBy, sOrder = sortOrder) => {
    setLoading(true);
    try {
      try {
        const res = await listingsApi.getListings({
          q: searchQuery.trim() || undefined,
          status: statusFilter && statusFilter !== 'all' ? statusFilter : undefined,
          sort_by: sBy,
          sort_order: sOrder,
          page_size: 500
        });
        if (res && res.items) {
          setRows(res.items as Listing[]);
          setTotalCount(res.total);
          setLoading(false);
          return;
        }
      } catch (apiErr) {
        console.warn('FastAPI listings query error, falling back to Supabase:', apiErr);
      }

      let query = supabase
        .from('listings')
        .select('*', { count: 'exact' })
        .order('updated_at', { ascending: false })
        .limit(1000);

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (searchQuery.trim()) {
        const clean = searchQuery.trim();
        query = query.or(`name.ilike.%${clean}%,city.ilike.%${clean}%,category.ilike.%${clean}%`);
      }

      const { data, count, error } = await query;
      if (error) {
        console.error('Listings fetch error:', error);
      }
      if (data) {
        setRows(data as Listing[]);
      }
      if (count !== null && count !== undefined) {
        setTotalCount(count);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  // Initial load
  useEffect(() => {
    fetchRows(q, status);
  }, [status]);

  // Debounced search query
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchRows(q, status);
    }, 300);
    return () => clearTimeout(handler);
  }, [q]);

function matchCategory(listingCategory: string | null | undefined, listingName: string | null | undefined, selectedCategory: string): boolean {
  if (!listingCategory && !listingName) return false;
  const lCat = (listingCategory || '').toLowerCase().trim();
  const lName = (listingName || '').toLowerCase().trim();
  const sCat = selectedCategory.toLowerCase().trim();
  if (!sCat) return true;

  if (lCat === sCat) return true;
  if (lCat.includes(sCat) || sCat.includes(lCat)) return true;
  if (lName.includes(sCat)) return true;

  // Category synonyms
  if (sCat.includes('clinic') && (lCat.includes('health') || lCat.includes('doctor') || lCat.includes('clinic'))) return true;
  if (sCat.includes('health') && (lCat.includes('clinic') || lCat.includes('hospital') || lCat.includes('doctor'))) return true;
  if (sCat.includes('hotel') && (lCat.includes('hotel') || lCat.includes('resort') || lCat.includes('stay'))) return true;
  if (sCat.includes('car') && (lCat.includes('car') || lCat.includes('auto'))) return true;
  return false;
}

function matchCity(listingCity: string | null | undefined, listingAddress: string | null | undefined, selectedCity: string): boolean {
  if (!listingCity && !listingAddress) return false;
  const lCity = (listingCity || '').toLowerCase().trim();
  const lAddr = (listingAddress || '').toLowerCase().trim();
  const sCity = selectedCity.toLowerCase().trim();
  if (!sCity) return true;

  if (lCity === sCity) return true;
  if (lCity.includes(sCity) || sCity.includes(lCity)) return true;
  if (lAddr.includes(sCity)) return true;

  // Metro Neighborhood Aliases
  const neighborhoodMap: Record<string, string[]> = {
    'chennai': ['t nagar', 't. nagar', 'adyar', 'velachery', 'mylapore', 'kodambakkam', 'guindy', 'porur'],
    'mumbai': ['thane', 'vashi', 'powai', 'bandra', 'andheri', 'hiranandani'],
    'delhi ncr': ['noida', 'gurgaon', 'dwarka', 'rohini', 'connaught place', 'delhi-ncr']
  };

  const aliases = neighborhoodMap[sCity] || [];
  if (aliases.some(alias => lCity.includes(alias) || lAddr.includes(alias))) return true;

  return false;
}

  const availableCategoriesList = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.category) set.add(r.category); });
    BUSINESS_CATEGORIES.forEach(c => set.add(c));
    dbCategories.forEach(c => set.add(c));
    const list = Array.from(set).sort((a, b) => {
      const countA = rows.filter(r => matchCategory(r.category, r.name, a)).length;
      const countB = rows.filter(r => matchCategory(r.category, r.name, b)).length;
      return countB - countA;
    });
    if (!catSearchText.trim()) return list;
    return list.filter(c => c.toLowerCase().includes(catSearchText.toLowerCase()));
  }, [rows, catSearchText, dbCategories]);

  const availableCitiesList = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.city) set.add(r.city); });
    INDIAN_CITIES.forEach(c => set.add(c));
    const list = Array.from(set).sort((a, b) => {
      const countA = rows.filter(r => matchCity(r.city, r.address, a)).length;
      const countB = rows.filter(r => matchCity(r.city, r.address, b)).length;
      return countB - countA;
    });
    if (!citySearchText.trim()) return list;
    return list.filter(c => c.toLowerCase().includes(citySearchText.toLowerCase()));
  }, [rows, citySearchText]);

  const searchSuggestions = useMemo(() => {
    const text = q.trim().toLowerCase();
    if (!text) return { categories: [], cities: [], listings: [] };

    const matchingCats = availableCategoriesList.filter(c => c.toLowerCase().includes(text)).slice(0, 5);
    const matchingCities = availableCitiesList.filter(c => c.toLowerCase().includes(text)).slice(0, 5);
    const matchingListings = rows.filter(r => r.name.toLowerCase().includes(text)).slice(0, 5);

    return { categories: matchingCats, cities: matchingCities, listings: matchingListings };
  }, [q, availableCategoriesList, availableCitiesList, rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (status !== 'all' && r.status !== status) return false;

      // Elite Filter
      if (eliteFilter !== 'all') {
        const isElite = !!((r as any).is_elite || (r as any).is_featured || (r.schema_json as any)?.is_elite);
        if (eliteFilter === 'elite' && !isElite) return false;
        if (eliteFilter === 'normal' && isElite) return false;
      }

      // Smart Multi-Select Category Filter
      if (selectedCategories.length > 0) {
        const hasMatch = selectedCategories.some(cat => matchCategory(r.category, r.name, cat));
        if (!hasMatch) return false;
      }

      // Smart Multi-Select City Filter
      if (selectedCities.length > 0) {
        const hasMatch = selectedCities.some(city => matchCity(r.city, r.address, city));
        if (!hasMatch) return false;
      }
      
      // Date Filter Calculations
      if (dateFilter !== 'all') {
        const created = new Date(r.created_at);
        const today = new Date();
        if (dateFilter === 'today') {
          if (!isSameDay(created, today)) return false;
        } else if (dateFilter === 'yesterday') {
          if (!isSameDay(created, getYesterday())) return false;
        } else if (dateFilter === '7days') {
          const limit = new Date();
          limit.setDate(limit.getDate() - 7);
          if (created < limit) return false;
        } else if (dateFilter === 'this_month') {
          if (created.getMonth() !== today.getMonth() || created.getFullYear() !== today.getFullYear()) return false;
        } else if (dateFilter === 'last_month') {
          const prev = new Date();
          prev.setMonth(prev.getMonth() - 1);
          if (created.getMonth() !== prev.getMonth() || created.getFullYear() !== prev.getFullYear()) return false;
        } else if (dateFilter === 'custom') {
          if (customStart) {
            const start = new Date(customStart);
            start.setHours(0, 0, 0, 0);
            if (created < start) return false;
          }
          if (customEnd) {
            const end = new Date(customEnd);
            end.setHours(23, 59, 59, 999);
            if (created > end) return false;
          }
        }
      }

      if (q) {
        const s = q.toLowerCase();
        return (
          r.name.toLowerCase().includes(s) ||
          (r.city ?? '').toLowerCase().includes(s) ||
          (r.category ?? '').toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [rows, q, status, dateFilter, customStart, customEnd, selectedCategories, selectedCities, eliteFilter]);

  // Apply sorting keys
  const sortedAndFiltered = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let valA: any = a[sortBy as keyof Listing];
      let valB: any = b[sortBy as keyof Listing];

      if (sortBy === 'created_at' || sortBy === 'updated_at') {
        valA = new Date(valA || 0).getTime();
        valB = new Date(valB || 0).getTime();
      } else if (sortBy === 'view_count') {
        valA = Number(valA || 0);
        valB = Number(valB || 0);
      } else {
        valA = (valA || '').toString().toLowerCase();
        valB = (valB || '').toString().toLowerCase();
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortBy, sortOrder]);

  const totalItems = sortedAndFiltered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedListings = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedAndFiltered.slice(start, start + pageSize);
  }, [sortedAndFiltered, currentPage, pageSize]);

  const toggleSort = (key: string) => {
    let nextOrder: 'asc' | 'desc' = 'desc';
    let nextKey = key;
    if (sortBy === key) {
      nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortBy(nextKey);
    setSortOrder(nextOrder);
    fetchRows(q, status, nextKey, nextOrder);
  };

  const publish = async (l: Listing) => {
    try {
      await listingsApi.updateListing(l.id, { status: 'published', pipeline_stage: 'publish' });
    } catch {}
    
    // Update local rows immediately
    setRows((r) => r.map((x) => (x.id === l.id ? { ...x, status: 'published', pipeline_stage: 'publish' } : x)));
    if (open?.id === l.id) {
      setOpen({ ...open, status: 'published', pipeline_stage: 'publish' });
    }
  };

  // Bulk Actions
  const handleBulkApprove = async () => {
    if (!window.confirm(`Are you sure you want to approve and publish ${selectedIds.length} listings?`)) return;
    try {
      await Promise.all(selectedIds.map(id => listingsApi.updateListing(id, { status: 'published', pipeline_stage: 'publish' }).catch(() => {})));
      setRows(prev =>
        prev.map(item =>
          selectedIds.includes(item.id) ? { ...item, status: 'published', pipeline_stage: 'publish' } : item
        )
      );
      setSelectedIds([]);
    } catch (e) {
      console.error(e);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} listings?`)) return;
    try {
      await supabase.from('listings').delete().in('id', selectedIds);
      setRows(prev => prev.filter(item => !selectedIds.includes(item.id)));
      setSelectedIds([]);
    } catch (e) {
      console.error(e);
    }
  };

  // Callback to update a listing row in Listings view after drawer sub-actions
  const handleListingUpdate = (updated: Listing) => {
    setRows(prev => prev.map(x => x.id === updated.id ? updated : x));
    setOpen(updated);
  };

  return (
    <div className="space-y-4 bg-white text-slate-800 p-6 rounded-3xl min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeader
          title="Ingested Listings"
          subtitle={`${totalCount !== null ? totalCount.toLocaleString() : rows.length.toLocaleString()} total records in database. Displaying latest ${rows.length.toLocaleString()} records.`}
        />
        <button
          type="button"
          onClick={() => fetchRows(q, status)}
          disabled={loading}
          className="self-start sm:self-auto px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer shadow-xs"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin text-emerald-600' : 'text-slate-600'}`} />
          <span>{loading ? 'Refreshing...' : 'Refresh Listings'}</span>
        </button>
      </div>

      {/* Filter and Search Panel */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-grow min-w-64 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setShowSearchDropdown(true);
                }}
                onFocus={() => setShowSearchDropdown(true)}
                placeholder="Search listings by name, city or category..."
                className="w-full pl-9 pr-8 py-2 rounded-xl bg-white border border-slate-200 focus:border-emerald-600 text-xs font-semibold text-slate-800 placeholder-slate-400 outline-none transition"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => { setQ(''); setShowSearchDropdown(false); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold cursor-pointer"
                >
                  ✕
                </button>
              )}

              {/* Autocomplete Search Suggestion Dropdown Overlay */}
              {showSearchDropdown && q.trim().length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 p-3 space-y-3 text-xs font-semibold animate-fade-in">
                  {/* Matching Categories */}
                  {searchSuggestions.categories.length > 0 && (
                    <div>
                      <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                        <Tag className="h-3 w-3 text-emerald-600" /> Filter by Category
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {searchSuggestions.categories.map((cat) => (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => {
                              setSelectedCategories([cat]);
                              setQ('');
                              setShowSearchDropdown(false);
                            }}
                            className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1"
                          >
                            <span>+ {cat}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Matching Cities */}
                  {searchSuggestions.cities.length > 0 && (
                    <div>
                      <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-emerald-600" /> Filter by City
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {searchSuggestions.cities.map((city) => (
                          <button
                            key={city}
                            type="button"
                            onClick={() => {
                              setSelectedCities([city]);
                              setQ('');
                              setShowSearchDropdown(false);
                            }}
                            className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1"
                          >
                            <span>+ {city}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Matching Business Listings */}
                  {searchSuggestions.listings.length > 0 && (
                    <div>
                      <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Matching Business Names</div>
                      <div className="space-y-1">
                        {searchSuggestions.listings.map((l) => (
                          <div
                            key={l.id}
                            onClick={() => {
                              setQ(l.name);
                              setShowSearchDropdown(false);
                            }}
                            className="p-1.5 hover:bg-emerald-50 rounded-lg cursor-pointer flex items-center justify-between transition"
                          >
                            <span className="font-bold text-slate-800 truncate">{l.name}</span>
                            <span className="text-[10px] text-slate-400 shrink-0 ml-2">{l.category} · {l.city}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {searchSuggestions.categories.length === 0 && searchSuggestions.cities.length === 0 && searchSuggestions.listings.length === 0 && (
                    <div className="text-center py-2 text-slate-400 text-xs font-bold">
                      No categories, cities or businesses matching "{q}"
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Status Selector */}
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shrink-0 shadow-sm">
              {statuses.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-all cursor-pointer ${
                    status === s ? 'bg-emerald-600 text-white font-bold shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {s.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Date Filters Dropdown */}
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span className="text-xs text-slate-500 font-bold">Date:</span>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value as any)}
                  className="bg-transparent text-xs font-bold text-slate-700 outline-none cursor-pointer"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="7days">Last 7 Days</option>
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {/* Custom Date Inputs */}
              {dateFilter === 'custom' && (
                <div className="flex items-center gap-2 animate-fade-in">
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 px-2 py-1 outline-none"
                  />
                  <span className="text-xs text-slate-400">to</span>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 px-2 py-1 outline-none"
                  />
                </div>
              )}

              {/* Multi-Select Category Filter Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setShowCategoryFilterPopover(!showCategoryFilterPopover);
                    setShowCityFilterPopover(false);
                  }}
                  className={`px-3 py-2 rounded-xl border text-xs font-extrabold flex items-center gap-1.5 transition cursor-pointer shadow-xs ${
                    selectedCategories.length > 0
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <Tag className="h-3.5 w-3.5" />
                  <span>Category {selectedCategories.length > 0 ? `(${selectedCategories.length})` : 'All'}</span>
                  <span className="text-[10px]">▼</span>
                </button>

                {showCategoryFilterPopover && (
                  <div className="absolute top-full left-0 mt-1.5 w-64 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 p-3 space-y-2 animate-fade-in text-xs font-semibold">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <span className="font-extrabold text-slate-900">Multi-Select Categories</span>
                      {selectedCategories.length > 0 && (
                        <button type="button" onClick={() => setSelectedCategories([])} className="text-[10px] text-red-600 font-bold hover:underline cursor-pointer">
                          Clear
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={catSearchText}
                      onChange={(e) => setCatSearchText(e.target.value)}
                      placeholder="Search category name..."
                      className="w-full h-8 px-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 outline-none"
                    />
                    <div className="max-h-48 overflow-y-auto space-y-1 scrollbar-thin">
                      {availableCategoriesList.map((cat) => {
                        const isChecked = selectedCategories.includes(cat);
                        const count = rows.filter(r => matchCategory(r.category, r.name, cat)).length;
                        return (
                          <label key={cat} className="flex items-center justify-between p-1.5 hover:bg-emerald-50 rounded-lg cursor-pointer text-slate-800">
                            <div className="flex items-center gap-2 truncate">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  setSelectedCategories(prev => isChecked ? prev.filter(x => x !== cat) : [...prev, cat]);
                                }}
                                className="rounded text-emerald-600 focus:ring-0 cursor-pointer"
                              />
                              <span className="truncate">{cat}</span>
                            </div>
                            <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full ml-1 shrink-0">{count}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Multi-Select City Filter Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setShowCityFilterPopover(!showCityFilterPopover);
                    setShowCategoryFilterPopover(false);
                  }}
                  className={`px-3 py-2 rounded-xl border text-xs font-extrabold flex items-center gap-1.5 transition cursor-pointer shadow-xs ${
                    selectedCities.length > 0
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <MapPin className="h-3.5 w-3.5" />
                  <span>City {selectedCities.length > 0 ? `(${selectedCities.length})` : 'All'}</span>
                  <span className="text-[10px]">▼</span>
                </button>

                {showCityFilterPopover && (
                  <div className="absolute top-full left-0 mt-1.5 w-64 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 p-3 space-y-2 animate-fade-in text-xs font-semibold">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <span className="font-extrabold text-slate-900">Multi-Select Cities</span>
                      {selectedCities.length > 0 && (
                        <button type="button" onClick={() => setSelectedCities([])} className="text-[10px] text-red-600 font-bold hover:underline cursor-pointer">
                          Clear
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={citySearchText}
                      onChange={(e) => setCitySearchText(e.target.value)}
                      placeholder="Search city name..."
                      className="w-full h-8 px-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 outline-none"
                    />
                    <div className="max-h-48 overflow-y-auto space-y-1 scrollbar-thin">
                      {availableCitiesList.map((city) => {
                        const isChecked = selectedCities.includes(city);
                        const count = rows.filter(r => matchCity(r.city, r.address, city)).length;
                        return (
                          <label key={city} className="flex items-center justify-between p-1.5 hover:bg-emerald-50 rounded-lg cursor-pointer text-slate-800">
                            <div className="flex items-center gap-2 truncate">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  setSelectedCities(prev => isChecked ? prev.filter(x => x !== city) : [...prev, city]);
                                }}
                                className="rounded text-emerald-600 focus:ring-0 cursor-pointer"
                              />
                              <span className="truncate">{city}</span>
                            </div>
                            <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full ml-1 shrink-0">{count}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Elite Status Filter Buttons */}
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-xs">
                <button
                  type="button"
                  onClick={() => setEliteFilter('all')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                    eliteFilter === 'all' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  All Status
                </button>
                <button
                  type="button"
                  onClick={() => setEliteFilter('elite')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-extrabold transition cursor-pointer flex items-center gap-1 ${
                    eliteFilter === 'elite' ? 'bg-amber-500 text-white' : 'text-amber-700 hover:bg-amber-50'
                  }`}
                >
                  <span>★ Elite Only</span>
                </button>
              </div>

              {/* Reset / Clear All Filters */}
              {(selectedCategories.length > 0 || selectedCities.length > 0 || eliteFilter !== 'all' || dateFilter !== 'all' || q) && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategories([]);
                    setSelectedCities([]);
                    setEliteFilter('all');
                    setDateFilter('all');
                    setQ('');
                  }}
                  className="text-xs font-extrabold text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-xl transition cursor-pointer flex items-center gap-1 shadow-xs"
                >
                  <span>Reset Filters ✕</span>
                </button>
              )}
            </div>

            <div className="text-xs font-bold text-slate-500">
              Matching Results: <span className="text-emerald-700 font-extrabold text-sm">{sortedAndFiltered.length}</span> listings
            </div>
          </div>
        </div>
      </div>

      {/* Bulk actions panel */}
      {selectedIds.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex items-center justify-between animate-fade-in shadow-sm">
          <div className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4 text-emerald-600" />
            <span className="text-xs font-bold text-slate-700">
              {selectedIds.length} listings selected
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleBulkApprove}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-4 py-2 rounded-xl shadow-sm transition cursor-pointer"
            >
              Approve Selected
            </button>
            <button
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs px-4 py-2 rounded-xl shadow-sm transition cursor-pointer"
            >
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Ingested Listings Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto shadow-sm">
        <table className="w-full text-xs text-left border-collapse min-w-[960px]">
          <thead className="bg-slate-100 text-slate-700 font-extrabold uppercase tracking-wider select-none border-b border-slate-300 sticky top-0 z-10 shadow-xs">
            <tr>
              <th className="px-4 py-3.5 w-10 text-center">
                <input
                  type="checkbox"
                  checked={sortedAndFiltered.length > 0 && selectedIds.length === sortedAndFiltered.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(sortedAndFiltered.map(l => l.id));
                    } else {
                      setSelectedIds([]);
                    }
                  }}
                  className="rounded text-emerald-600 focus:ring-0 cursor-pointer"
                />
              </th>
              <th className="px-4 py-3.5 w-[300px] cursor-pointer hover:text-slate-900 transition" onClick={() => toggleSort('name')}>
                Business Name {sortBy === 'name' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th className="px-4 py-3.5 w-[160px] cursor-pointer hover:text-slate-900 transition" onClick={() => toggleSort('category')}>
                Category {sortBy === 'category' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th className="px-4 py-3.5 w-[130px] cursor-pointer hover:text-slate-900 transition" onClick={() => toggleSort('city')}>
                City {sortBy === 'city' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th className="px-4 py-3.5 w-[90px] text-right cursor-pointer hover:text-slate-900 transition" onClick={() => toggleSort('view_count')}>
                Views {sortBy === 'view_count' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th className="px-4 py-3.5 w-[110px] cursor-pointer hover:text-slate-900 transition" onClick={() => toggleSort('created_at')}>
                Created {sortBy === 'created_at' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th className="px-4 py-3.5 w-[110px] cursor-pointer hover:text-slate-900 transition" onClick={() => toggleSort('updated_at')}>
                Updated {sortBy === 'updated_at' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th className="px-4 py-3.5 w-[110px] cursor-pointer hover:text-slate-900 transition" onClick={() => toggleSort('status')}>
                Status {sortBy === 'status' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
              </th>
              <th className="px-4 py-3.5 w-[110px] text-center">
                Elite Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
            {paginatedListings.map((l) => (
              <tr
                key={l.id}
                onClick={() => setOpen(l)}
                className="cursor-pointer hover:bg-slate-50 transition"
              >
                <td className="px-4 py-3 w-10 text-center" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(l.id)}
                    onChange={() => {
                      setSelectedIds(prev =>
                        prev.includes(l.id) ? prev.filter(id => id !== l.id) : [...prev, l.id]
                      );
                    }}
                    className="rounded text-emerald-600 focus:ring-0 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 max-w-[280px]">
                  <div className="flex items-center gap-3">
                    <img
                      src={(l.schema_json as any)?.logo || l.hero_image_url || 'https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=100&q=80'}
                      alt=""
                      className="h-9 w-9 rounded-xl object-cover border border-slate-200 bg-slate-50 shrink-0"
                    />
                    <div className="min-w-0 text-left flex-1">
                      <div className="text-slate-900 font-bold truncate text-xs flex items-center gap-1.5">
                        <a
                          href={`/b/${l.slug || l.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-blue-600 hover:underline flex items-center gap-1 truncate text-slate-900"
                          title="Click to view live published page in new tab"
                        >
                          <span className="truncate">{l.name}</span>
                          <ExternalLink className="h-3 w-3 text-blue-500 hover:text-blue-700 shrink-0 inline" />
                        </a>
                        {((l as any).is_elite || (l as any).is_featured || (l.schema_json as any)?.is_elite) && (
                          <span className="text-amber-500 font-extrabold text-[11px] shrink-0" title="Elite Featured Business">★</span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 truncate mt-0.5">{l.website || l.phone || l.email || '—'}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600 truncate max-w-[150px]">{l.category}</td>
                <td className="px-4 py-3 text-slate-600 truncate max-w-[120px]">{l.city}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">{l.view_count.toLocaleString()}</td>
                <td className="px-4 py-3 text-slate-500 font-mono text-[10px]">
                  {new Date(l.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                </td>
                <td className="px-4 py-3 text-slate-400 font-mono text-[10px]">{timeAgo(l.updated_at)}</td>
                <td className="px-4 py-3">
                  <Badge tone={l.status === 'published' ? 'success' : l.status === 'pending_approval' ? 'warn' : l.status === 'review' ? 'warn' : 'neutral'}>
                    {l.status.replace('_', ' ')}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-center gap-1.5 mx-auto">
                    <button
                      type="button"
                      onClick={() => setOpen(l)}
                      className="px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-slate-900 text-white hover:bg-slate-800 transition flex items-center gap-1 shadow-xs cursor-pointer"
                      title="Edit this listing"
                    >
                      <Edit3 className="h-3 w-3" />
                      <span>Edit</span>
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        const currentElite = !!((l as any).is_elite || (l as any).is_featured || (l.schema_json as any)?.is_elite);
                        const nextVal = !currentElite;
                        const updatedSchema = { ...(l.schema_json as any), is_elite: nextVal };
                        try {
                          await listingsApi.toggleElite(l.id);
                        } catch (err) {
                          console.warn('FastAPI toggle elite error, trying updateListing:', err);
                          try {
                            await listingsApi.updateListing(l.id, { is_elite: nextVal, is_featured: nextVal, schema_json: updatedSchema });
                          } catch {}
                        }
                        setRows(prev => prev.map(r => r.id === l.id ? { ...r, is_elite: nextVal, is_featured: nextVal, schema_json: updatedSchema } as any : r));
                      }}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold transition-all cursor-pointer flex items-center justify-center gap-1 ${
                        (l as any).is_elite || (l as any).is_featured || (l.schema_json as any)?.is_elite
                          ? 'bg-amber-500 text-white shadow-sm hover:bg-amber-600'
                          : 'bg-slate-100 text-slate-500 hover:bg-amber-100 hover:text-amber-800'
                      }`}
                    >
                      <span>{(l as any).is_elite || (l as any).is_featured || (l.schema_json as any)?.is_elite ? '★ ELITE' : '☆ MAKE ELITE'}</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {loading ? (
        <div className="py-16 flex flex-col items-center justify-center gap-3 bg-white border border-slate-200 rounded-2xl shadow-xs">
          <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
          <span className="text-xs font-bold text-slate-600">Loading listings from database...</span>
        </div>
      ) : sortedAndFiltered.length === 0 ? (
        <Empty title="No listings match query" hint="Try adjusting status filter chips or query dates" />
      ) : null}

      {/* Pagination Footer & Page Size Selector */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-50 border border-slate-200 rounded-2xl p-4 mt-4">
        <div className="flex items-center gap-3 text-xs text-slate-600 font-bold">
          <span>Show per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="bg-white border border-slate-200 rounded-xl text-xs font-bold px-3 py-1.5 outline-none cursor-pointer shadow-sm text-slate-800"
          >
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
            <option value={200}>200 per page</option>
            <option value={500}>500 per page</option>
            <option value={1000}>1000 per page</option>
          </select>
          <span className="text-slate-500 font-semibold">
            (Showing {Math.min(totalItems, (currentPage - 1) * pageSize + 1)} - {Math.min(totalItems, currentPage * pageSize)} of {totalItems} listings)
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3.5 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 text-xs font-bold flex items-center gap-1 cursor-pointer transition shadow-sm"
          >
            Previous Page
          </button>
          <span className="text-xs font-bold text-slate-700 px-3">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3.5 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 text-xs font-bold flex items-center gap-1 cursor-pointer transition shadow-sm"
          >
            Next Page
          </button>
        </div>
      </div>

      {open && (
        <ListingDrawer
          listing={open}
          dbCategories={dbCategories}
          onClose={() => setOpen(null)}
          onPublish={() => publish(open)}
          onUpdate={handleListingUpdate}
        />
      )}
    </div>
  );
}

function ListingDrawer({
  listing,
  dbCategories = [],
  onClose,
  onPublish,
  onUpdate
}: {
  listing: Listing;
  dbCategories?: string[];
  onClose: () => void;
  onPublish: () => void;
  onUpdate: (updated: Listing) => void;
}) {
  const seoTitle = `${listing.name} — ${listing.category} in ${listing.city}`;
  const seoDesc = listing.ai_summary ?? `Contact ${listing.name}, a top ${listing.category?.toLowerCase()} in ${listing.city}. Reviews, hours and directions.`;
  const faq = [
    { q: `Where is ${listing.name} located?`, a: `${listing.address}, ${listing.city}, ${listing.state}.` },
    { q: `How can I contact ${listing.name}?`, a: `Call ${listing.phone} or email ${listing.email}.` },
    { q: `Does ${listing.name} take walk-ins?`, a: 'Walk-ins are welcome during regular business hours; appointments are recommended.' },
  ];
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: listing.name,
    telephone: listing.phone,
    email: listing.email,
    url: listing.website,
    address: {
      '@type': 'PostalAddress',
      streetAddress: listing.address,
      addressLocality: listing.city,
      addressRegion: listing.state,
      addressCountry: listing.country,
    },
    geo: { '@type': 'GeoCoordinates', latitude: listing.latitude, longitude: listing.longitude },
  };

  const pendingOffers = useMemo(() => {
    const list = (listing.schema_json as any)?.offers || [];
    return list.filter((o: any) => o.status === 'pending_approval');
  }, [listing.schema_json]);

  const pendingEvents = useMemo(() => {
    const list = (listing.schema_json as any)?.events || [];
    return list.filter((e: any) => e.status === 'pending_approval');
  }, [listing.schema_json]);

  const approveOfferEvent = async (id: string, type: 'Offer' | 'Event') => {
    const schemaUpdate = { ...(listing.schema_json as any) };
    if (type === 'Offer') {
      schemaUpdate.offers = schemaUpdate.offers.map((o: any) => o.id === id ? { ...o, status: 'approved' } : o);
    } else {
      schemaUpdate.events = schemaUpdate.events.map((e: any) => e.id === id ? { ...e, status: 'approved' } : e);
    }

    try {
      await listingsApi.updateListing(listing.id, { schema_json: schemaUpdate });
      onUpdate({ ...listing, schema_json: schemaUpdate });
    } catch (e) {
      console.error(e);
    }
  };

  // Extract owner submitted data
  const logo = (listing.schema_json as any)?.logo;
  const gallery = Array.isArray(listing.images) ? listing.images : [];
  const social = (listing.schema_json as any)?.social_links || {};
  const youtube = (listing.schema_json as any)?.youtube_links || [];
  const mapLink = (listing.schema_json as any)?.google_map_link;
  const registeredCities = (listing.schema_json as any)?.original_cities || [listing.city];
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState(listing.name || '');
  const [editCategory, setEditCategory] = useState(listing.category || '');
  const [editPhone, setEditPhone] = useState(listing.phone || '');
  const [editWhatsapp, setEditWhatsapp] = useState((listing.schema_json as any)?.whatsapp_number || '');
  const [editEmail, setEditEmail] = useState(listing.email || '');
  const [editWebsite, setEditWebsite] = useState(listing.website || '');
  const [editAddress, setEditAddress] = useState(listing.address || '');
  const [editCity, setEditCity] = useState(listing.city || '');
  const [editSummary, setEditSummary] = useState(listing.ai_summary || '');
  const [editLogo, setEditLogo] = useState((listing.schema_json as any)?.logo || '');
  const [editHero, setEditHero] = useState(listing.hero_image_url || '');
  const [editGallery, setEditGallery] = useState<string[]>(Array.isArray(listing.images) ? listing.images : []);
  const [savingEdit, setSavingEdit] = useState(false);

  const [isElite, setIsElite] = useState<boolean>(() => {
    return !!(listing as any).is_elite || !!(listing as any).is_featured || !!(listing.schema_json as any)?.is_elite;
  });
  const [editCategorySearch, setEditCategorySearch] = useState('');
  const [showCategoryPopover, setShowCategoryPopover] = useState(false);
  const [editCitySearch, setEditCitySearch] = useState('');
  const [showCityPopover, setShowCityPopover] = useState(false);
  const [editCitiesList, setEditCitiesList] = useState<string[]>(
    registeredCities && registeredCities.length > 0 ? registeredCities : [listing.city]
  );

  const [editMapLink, setEditMapLink] = useState((listing.schema_json as any)?.google_map_link || '');
  const [editSlug, setEditSlug] = useState(listing.slug || '');
  const [editLat, setEditLat] = useState(listing.latitude !== undefined && listing.latitude !== null ? String(listing.latitude) : '');
  const [editLng, setEditLng] = useState(listing.longitude !== undefined && listing.longitude !== null ? String(listing.longitude) : '');

  const handleMapLinkChange = (url: string) => {
    setEditMapLink(url);
    if (!editLat || !editLng) {
      const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (atMatch) {
        setEditLat(atMatch[1]);
        setEditLng(atMatch[2]);
      } else {
        const qMatch = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
        if (qMatch) {
          setEditLat(qMatch[1]);
          setEditLng(qMatch[2]);
        } else {
          const dMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
          if (dMatch) {
            setEditLat(dMatch[1]);
            setEditLng(dMatch[2]);
          }
        }
      }
    }
  };

  const [editYoutube, setEditYoutube] = useState<string[]>(
    Array.isArray((listing.schema_json as any)?.youtube_links) && (listing.schema_json as any)?.youtube_links.length > 0
      ? (listing.schema_json as any)?.youtube_links
      : ['']
  );
  const [editSocial, setEditSocial] = useState<{ facebook?: string; instagram?: string; twitter?: string; linkedin?: string }>(
    (listing.schema_json as any)?.social_links || {}
  );

  const allCategoryOptions = useMemo(() => {
    const set = new Set<string>();
    BUSINESS_CATEGORIES.forEach(c => set.add(c));
    dbCategories.forEach(c => set.add(c));
    return Array.from(set);
  }, [dbCategories]);

  const filteredCategoryList = useMemo(() => {
    if (!editCategorySearch) return allCategoryOptions.slice(0, 30);
    return allCategoryOptions.filter(c => c.toLowerCase().includes(editCategorySearch.toLowerCase())).slice(0, 30);
  }, [editCategorySearch, allCategoryOptions]);

  const filteredCityList = useMemo(() => {
    if (!editCitySearch) return INDIAN_CITIES.slice(0, 30);
    return INDIAN_CITIES.filter(c => c.toLowerCase().includes(editCitySearch.toLowerCase())).slice(0, 30);
  }, [editCitySearch]);

  const handleSaveListing = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingEdit(true);

    const updatedSchema = {
      ...(listing.schema_json as any),
      logo: editLogo,
      original_cities: editCitiesList,
      whatsapp_number: editWhatsapp.trim() || null,
      google_map_link: editMapLink,
      youtube_links: editYoutube.filter(x => !!x.trim()),
      video_url: editYoutube.find(x => !!x.trim()) || null,
      social_links: editSocial,
      is_elite: isElite
    };

    const parsedLat = editLat.trim() ? parseFloat(editLat.trim()) : null;
    const parsedLng = editLng.trim() ? parseFloat(editLng.trim()) : null;

    const updates = {
      name: editName,
      slug: editSlug.trim() || listing.slug,
      category: editCategory,
      phone: editPhone,
      email: editEmail,
      website: editWebsite,
      address: editAddress,
      city: editCitiesList[0] || editCity,
      latitude: !isNaN(parsedLat as number) ? parsedLat : listing.latitude,
      longitude: !isNaN(parsedLng as number) ? parsedLng : listing.longitude,
      ai_summary: editSummary,
      hero_image_url: editHero,
      images: editGallery.filter(x => !!x),
      schema_json: updatedSchema,
      is_elite: isElite,
      is_featured: isElite,
      updated_at: new Date().toISOString()
    };

    try {
      await listingsApi.updateListing(listing.id, updates);
    } catch (apiErr) {
      console.error('Failed to update listing via backend API:', apiErr);
    }

    const updatedListing = { ...listing, ...updates };
    onUpdate(updatedListing);
    setSavingEdit(false);
    setShowEditModal(false);
    alert('Listing information & images updated successfully!');
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-30 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl h-full overflow-y-auto scrollbar-thin bg-white border-l border-slate-200 shadow-2xl text-slate-800" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-slate-50 border-b border-slate-200 p-5 flex items-center justify-between z-10">
          <div>
            <div className="text-base font-bold text-slate-900">{listing.name}</div>
            <div className="text-xs text-slate-500 font-semibold">{listing.category} · {listing.city}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-3.5 py-2 rounded-xl shadow-sm transition flex items-center gap-1.5 cursor-pointer"
            >
              <Edit3 className="h-3.5 w-3.5" /> Edit Listing
            </button>
            {listing.status !== 'published' && (
              <button
                onClick={onPublish}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-4 py-2 rounded-xl shadow-sm transition cursor-pointer"
              >
                Approve & Publish
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1.5 transition"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {/* Super Admin Edit Listing Modal Overlay */}
        {showEditModal && (
          <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowEditModal(false)}>
            <div className="bg-white border border-slate-200 rounded-3xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl space-y-4 text-slate-800" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Edit3 className="h-4 w-4 text-emerald-600" /> Edit Listing Details & Images
                </h3>
                <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
              </div>

              <form onSubmit={handleSaveListing} className="space-y-4 text-xs font-semibold text-slate-700">
                {/* Super Admin Elite Featured Business Toggle */}
                <div className="bg-amber-50 border border-amber-200 p-3.5 rounded-2xl flex items-center justify-between shadow-xs">
                  <div className="flex items-center gap-2.5">
                    <span className="material-symbols-outlined text-amber-600 text-xl">workspace_premium</span>
                    <div>
                      <div className="font-extrabold text-xs text-amber-950">Elite Featured Business Status</div>
                      <div className="text-[10px] text-amber-800 font-semibold">Display this listing in the Homepage Elite Collection</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsElite(!isElite)}
                    className={`px-3.5 py-1.5 rounded-xl font-extrabold text-xs transition cursor-pointer flex items-center gap-1 shadow-sm ${
                      isElite ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                    }`}
                  >
                    <span>{isElite ? '★ ELITE ACTIVE' : '☆ MAKE ELITE'}</span>
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-900 mb-1">Business Name *</label>
                    <input value={editName} onChange={(e) => setEditName(e.target.value)} className="w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-bold outline-none" />
                  </div>

                  {/* Category Search Dropdown */}
                  <div className="relative">
                    <label className="block text-[11px] font-bold text-slate-900 mb-1">Business Category *</label>
                    <input
                      value={editCategorySearch || editCategory}
                      onChange={(e) => {
                        setEditCategorySearch(e.target.value);
                        setShowCategoryPopover(true);
                      }}
                      onFocus={() => setShowCategoryPopover(true)}
                      placeholder="Search category..."
                      className="w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-bold outline-none focus:border-emerald-600"
                    />
                    {showCategoryPopover && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowCategoryPopover(false)} />
                        <div className="absolute top-full left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-2xl z-50 p-2 space-y-1">
                          {filteredCategoryList.map((cat) => (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => {
                                setEditCategory(cat);
                                setEditCategorySearch('');
                                setShowCategoryPopover(false);
                              }}
                              className="w-full text-left px-3 py-1.5 rounded-lg text-xs font-bold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition cursor-pointer"
                            >
                              {cat}
                            </button>
                          ))}
                          {editCategorySearch.trim() && !filteredCategoryList.some(c => c.toLowerCase() === editCategorySearch.trim().toLowerCase()) && (
                            <button
                              type="button"
                              onClick={async () => {
                                const newCat = editCategorySearch.trim();
                                try {
                                  await categoriesApi.createCategory({ name: newCat, slug: slugify(newCat) });
                                } catch (catErr) {
                                  console.warn('Failed to save category to backend:', catErr);
                                }
                                setEditCategory(newCat);
                                setEditCategorySearch('');
                                setShowCategoryPopover(false);
                              }}
                              className="w-full text-left px-3 py-2 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition cursor-pointer border-t border-emerald-200 flex items-center gap-1.5"
                            >
                              <span>✨ Super Admin: + Create new category "{editCategorySearch.trim()}"</span>
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-900 mb-1">Phone Number *</label>
                    <input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="e.g. 075501 43234" className="w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-bold outline-none" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-900 mb-1">WhatsApp Number (Optional)</label>
                    <input value={editWhatsapp} onChange={(e) => setEditWhatsapp(e.target.value)} placeholder="e.g. 9940511931" className="w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-bold outline-none" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-900 mb-1">Email ID (Optional)</label>
                    <input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="e.g. contact@business.com" className="w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-bold outline-none" />
                  </div>
                </div>

                {/* Live URL Slug Manager */}
                <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-2xl space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold text-slate-900">Live URL Slug *</label>
                    <button
                      type="button"
                      onClick={() => {
                        const cityPart = editCitiesList[0] || editCity || 'chennai';
                        setEditSlug(`${slugify(editName)}-${slugify(cityPart)}-${Date.now().toString().slice(-6)}`.replace(/--+/g, '-'));
                      }}
                      className="text-[10px] text-emerald-700 font-bold hover:underline cursor-pointer flex items-center gap-1"
                    >
                      <Sparkles className="h-3 w-3" /> Auto-Generate from Name & City
                    </button>
                  </div>
                  <input
                    value={editSlug}
                    onChange={(e) => setEditSlug(slugify(e.target.value))}
                    placeholder="e.g. om-car-parking-rental-arumbakkam-chennai"
                    className="w-full h-10 px-3 bg-white border border-slate-300 rounded-xl text-slate-900 font-bold outline-none text-xs"
                  />
                  <div className="text-[10px] text-slate-500 font-semibold truncate">
                    Live URL: <span className="text-emerald-700 font-bold">https://zhasearch.com/b/{editSlug || '...'}</span>
                  </div>
                </div>

                {/* Multi-Select Cities Dropdown */}
                <div className="relative">
                  <label className="block text-[11px] font-bold text-slate-900 mb-1">Service Locations / Operating Cities (Multi-Select) *</label>
                  <input
                    value={editCitySearch}
                    onChange={(e) => {
                      setEditCitySearch(e.target.value);
                      setShowCityPopover(true);
                    }}
                    onFocus={() => setShowCityPopover(true)}
                    placeholder="Search and add operating cities (e.g. Chennai, Mumbai, Delhi)..."
                    className="w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-bold outline-none focus:border-emerald-600"
                  />
                  {showCityPopover && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowCityPopover(false)} />
                      <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-2xl z-50 p-2 space-y-1">
                        {filteredCityList.map((c) => {
                          const isSelected = editCitiesList.includes(c);
                          return (
                            <button
                              key={c}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  setEditCitiesList(prev => prev.filter(x => x !== c));
                                } else {
                                  setEditCitiesList(prev => [...prev, c]);
                                }
                                setEditCitySearch('');
                                setShowCityPopover(false);
                              }}
                              className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center justify-between cursor-pointer ${
                                isSelected ? 'bg-emerald-100 text-emerald-800' : 'hover:bg-slate-100 text-slate-700'
                              }`}
                            >
                              <span>{c}</span>
                              {isSelected && <span className="text-emerald-700 font-bold">✓ Selected</span>}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Selected Cities Tag Chips */}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {editCitiesList.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-800 border border-emerald-300 rounded-lg text-xs font-bold">
                        {c}
                        <button
                          type="button"
                          onClick={() => setEditCitiesList(prev => prev.filter(x => x !== c))}
                          className="hover:text-red-600 font-bold ml-1 cursor-pointer"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-900 mb-1">Website URL</label>
                  <input value={editWebsite} onChange={(e) => setEditWebsite(e.target.value)} className="w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-bold outline-none" />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-900 mb-1">Full Street Address</label>
                  <input value={editAddress} onChange={(e) => setEditAddress(e.target.value)} className="w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-bold outline-none" />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-900 mb-1">AI Summary / Description</label>
                  <textarea value={editSummary} onChange={(e) => setEditSummary(e.target.value)} rows={3} className="w-full p-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-bold outline-none resize-none" />
                </div>

                {/* Map Link, YouTube & Social Profiles */}
                <div className="border-t border-slate-100 pt-3 space-y-3">
                  <div className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-emerald-600" /> Map Directions, Video & Social Media
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 mb-1">Google Map Directions Link</label>
                    <input
                      value={editMapLink}
                      onChange={(e) => handleMapLinkChange(e.target.value)}
                      placeholder="https://maps.google.com/?q=... or https://goo.gl/maps/..."
                      className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-semibold outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 mb-1">Latitude (Map Pin)</label>
                      <input
                        type="number"
                        step="any"
                        value={editLat}
                        onChange={(e) => setEditLat(e.target.value)}
                        placeholder="e.g. 13.0694"
                        className="w-full h-8 px-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 mb-1">Longitude (Map Pin)</label>
                      <input
                        type="number"
                        step="any"
                        value={editLng}
                        onChange={(e) => setEditLng(e.target.value)}
                        placeholder="e.g. 80.2078"
                        className="w-full h-8 px-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 mb-1 flex items-center justify-between">
                      <span>YouTube Video URLs</span>
                      <button
                        type="button"
                        onClick={() => setEditYoutube(prev => [...prev, ''])}
                        className="text-emerald-700 hover:underline font-bold text-[10px] cursor-pointer"
                      >
                        + Add Video URL
                      </button>
                    </label>
                    {editYoutube.map((yUrl, idx) => (
                      <div key={idx} className="flex gap-2 mb-1.5">
                        <input
                          value={yUrl}
                          onChange={(e) => {
                            const updated = [...editYoutube];
                            updated[idx] = e.target.value;
                            setEditYoutube(updated);
                          }}
                          placeholder={`YouTube Video ${idx + 1} URL`}
                          className="flex-1 h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-semibold outline-none"
                        />
                        {editYoutube.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setEditYoutube(prev => prev.filter((_, i) => i !== idx))}
                            className="px-2 bg-slate-100 hover:bg-red-50 text-red-600 rounded-xl text-xs font-bold"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 mb-1">Facebook Profile</label>
                      <input
                        value={editSocial.facebook || ''}
                        onChange={(e) => setEditSocial({ ...editSocial, facebook: e.target.value })}
                        placeholder="https://facebook.com/..."
                        className="w-full h-8 px-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 mb-1">Instagram Profile</label>
                      <input
                        value={editSocial.instagram || ''}
                        onChange={(e) => setEditSocial({ ...editSocial, instagram: e.target.value })}
                        placeholder="https://instagram.com/..."
                        className="w-full h-8 px-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 mb-1">Twitter / X Profile</label>
                      <input
                        value={editSocial.twitter || ''}
                        onChange={(e) => setEditSocial({ ...editSocial, twitter: e.target.value })}
                        placeholder="https://twitter.com/..."
                        className="w-full h-8 px-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 mb-1">LinkedIn Profile</label>
                      <input
                        value={editSocial.linkedin || ''}
                        onChange={(e) => setEditSocial({ ...editSocial, linkedin: e.target.value })}
                        placeholder="https://linkedin.com/in/..."
                        className="w-full h-8 px-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-semibold outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Media Assets with Visual Thumbnail Previews & WebP Uploaders */}
                <div className="border-t border-slate-100 pt-3 space-y-4">
                  <div className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                    <ImageIcon className="h-4 w-4 text-emerald-600" /> Media Assets & Image Previews (Auto-converts to WebP)
                  </div>

                  {/* Logo Asset Card */}
                  <div className="bg-slate-50 border border-slate-200 p-3 rounded-2xl space-y-2">
                    <div className="flex justify-between items-center text-xs font-bold text-slate-800">
                      <span>Business Logo Asset</span>
                      {editLogo && (
                        <button type="button" onClick={() => setEditLogo('')} className="text-red-600 hover:underline text-[11px] font-bold cursor-pointer">
                          Remove Logo
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {editLogo ? (
                        <img src={editLogo} alt="Logo" className="w-16 h-16 object-contain rounded-xl border border-slate-200 bg-white p-1 shadow-sm shrink-0" />
                      ) : (
                        <div className="w-16 h-16 rounded-xl border border-dashed border-slate-300 bg-white flex flex-col items-center justify-center text-[10px] text-slate-400 font-bold shrink-0">
                          No Logo
                        </div>
                      )}
                      <div className="flex-1 space-y-1">
                        <label className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs px-3 py-1.5 rounded-lg shadow-sm cursor-pointer inline-flex items-center gap-1">
                          <Upload className="h-3.5 w-3.5" /> Upload Logo (WebP)
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              if (e.target.files?.[0]) {
                                const webp = await convertFileToWebP(e.target.files[0]);
                                setEditLogo(webp);
                              }
                            }}
                          />
                        </label>
                        <p className="text-[10px] text-slate-400">All image formats auto-convert to lightweight WebP.</p>
                      </div>
                    </div>
                  </div>

                  {/* Hero Cover Banner Card */}
                  <div className="bg-slate-50 border border-slate-200 p-3 rounded-2xl space-y-2">
                    <div className="flex justify-between items-center text-xs font-bold text-slate-800">
                      <span>Primary Hero Cover Banner</span>
                      {editHero && (
                        <button type="button" onClick={() => setEditHero('')} className="text-red-600 hover:underline text-[11px] font-bold cursor-pointer">
                          Remove Banner
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {editHero ? (
                        <img src={editHero} alt="Hero Cover" className="w-24 h-16 object-cover rounded-xl border border-slate-200 shadow-sm shrink-0" />
                      ) : (
                        <div className="w-24 h-16 rounded-xl border border-dashed border-slate-300 bg-white flex items-center justify-center text-[10px] text-slate-400 font-bold shrink-0">
                          No Banner
                        </div>
                      )}
                      <div className="flex-1 space-y-1">
                        <label className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs px-3 py-1.5 rounded-lg shadow-sm cursor-pointer inline-flex items-center gap-1">
                          <Upload className="h-3.5 w-3.5" /> Upload Banner (WebP)
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              if (e.target.files?.[0]) {
                                const webp = await convertFileToWebP(e.target.files[0]);
                                setEditHero(webp);
                              }
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Photo Gallery Grid */}
                  <div className="bg-slate-50 border border-slate-200 p-3 rounded-2xl space-y-2">
                    <div className="text-xs font-bold text-slate-800">Photo Gallery (up to 5 images)</div>
                    <div className="grid grid-cols-5 gap-2">
                      {[0, 1, 2, 3, 4].map((idx) => {
                        const imgUrl = editGallery[idx];
                        return (
                          <div key={idx} className="flex flex-col items-center gap-1">
                            {imgUrl ? (
                              <div className="relative aspect-square w-full rounded-xl overflow-hidden border border-slate-200 group">
                                <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...editGallery];
                                    updated[idx] = '';
                                    setEditGallery(updated);
                                  }}
                                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 text-[10px] font-bold flex items-center justify-center shadow"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <label className="aspect-square w-full rounded-xl border border-dashed border-slate-300 bg-white hover:bg-slate-100 transition flex flex-col items-center justify-center text-[10px] text-slate-400 font-bold cursor-pointer">
                                <span>+ Photo {idx + 1}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={async (e) => {
                                    if (e.target.files?.[0]) {
                                      const webp = await convertFileToWebP(e.target.files[0]);
                                      const updated = [...editGallery];
                                      updated[idx] = webp;
                                      setEditGallery(updated);
                                    }
                                  }}
                                />
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="pt-3 flex justify-end gap-2 border-t border-slate-100">
                  <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-slate-500 font-bold cursor-pointer">Cancel</button>
                  <button type="submit" disabled={savingEdit} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-2 rounded-xl transition shadow-sm cursor-pointer">
                    {savingEdit ? 'Saving Changes...' : 'Save Listing Details'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Submitted Media Assets Panel */}
        <div className="p-5 border-b border-slate-200 bg-slate-50/60 space-y-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-emerald-600" /> Submitted Media & Photo Gallery
          </div>

          <div className="flex flex-wrap items-start gap-4">
            {/* Logo */}
            {logo ? (
              <div className="flex flex-col items-center gap-1">
                <a href={logo} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-2xl border border-slate-200 bg-white p-1 shadow-sm hover:scale-105 transition">
                  <img src={logo} alt="Logo" className="w-full h-full object-contain rounded-xl" />
                </a>
                <span className="text-[10px] font-bold text-slate-500">Logo Asset</span>
              </div>
            ) : (
              <div className="w-16 h-16 rounded-2xl border border-dashed border-slate-300 bg-white flex flex-col items-center justify-center text-[10px] text-slate-400 font-bold text-center">
                No Logo
              </div>
            )}

            {/* Cover Image */}
            {listing.hero_image_url && (
              <div className="flex flex-col items-start gap-1 flex-1 min-w-[200px]">
                <a href={listing.hero_image_url} target="_blank" rel="noreferrer" className="w-full h-20 rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:opacity-95 transition">
                  <img src={listing.hero_image_url} alt="Cover" className="w-full h-full object-cover" />
                </a>
                <span className="text-[10px] font-bold text-slate-500">Primary Hero Banner</span>
              </div>
            )}
          </div>

          {/* Photo Gallery Grid */}
          {gallery.length > 0 && (
            <div>
              <div className="text-[11px] font-bold text-slate-700 mb-2">Attached Gallery Photos ({gallery.length}):</div>
              <div className="grid grid-cols-5 gap-2">
                {gallery.map((img, i) => (
                  <a
                    key={i}
                    href={img}
                    target="_blank"
                    rel="noreferrer"
                    className="aspect-square w-full rounded-xl overflow-hidden border border-slate-200 hover:border-emerald-600 hover:scale-105 transition shadow-sm bg-white"
                  >
                    <img src={img} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {!logo && !listing.hero_image_url && gallery.length === 0 && (
            <div className="text-xs text-slate-400 font-semibold italic py-2">
              No owner-uploaded images attached to this business profile.
            </div>
          )}
        </div>

        <div className="p-5 space-y-5">
          {/* Tagged pending deals approval panels */}
          {(pendingOffers.length > 0 || pendingEvents.length > 0) && (
            <Section title="Pending Deals Approval" icon={<Clock className="h-4 w-4 text-amber-500" />}>
              <div className="space-y-3 pt-1.5">
                {pendingOffers.map((o: any) => (
                  <div key={o.id} className="bg-white border border-slate-200 p-3 rounded-xl flex justify-between items-center shadow-sm">
                    <div className="flex gap-3 text-left">
                      {o.image && (
                        <img src={o.image} alt="" className="w-12 h-12 object-cover rounded-lg shrink-0 border border-slate-200 bg-slate-50" />
                      )}
                      <div>
                        <span className="text-[9px] bg-purple-100 text-purple-700 font-bold px-1.5 py-0.5 rounded uppercase">Offer</span>
                        <div className="text-xs font-bold text-slate-800 mt-1">{o.title}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{o.description}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => approveOfferEvent(o.id, 'Offer')}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-3 py-1.5 rounded-lg transition shrink-0"
                    >
                      Approve Offer
                    </button>
                  </div>
                ))}

                {pendingEvents.map((e: any) => (
                  <div key={e.id} className="bg-white border border-slate-200 p-3 rounded-xl flex justify-between items-center shadow-sm">
                    <div className="flex gap-3 text-left">
                      {e.image && (
                        <img src={e.image} alt="" className="w-12 h-12 object-cover rounded-lg shrink-0 border border-slate-200 bg-slate-50" />
                      )}
                      <div>
                        <span className="text-[9px] bg-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded uppercase">Event</span>
                        <div className="text-xs font-bold text-slate-800 mt-1">{e.title}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{e.description}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => approveOfferEvent(e.id, 'Event')}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-3 py-1.5 rounded-lg transition shrink-0"
                    >
                      Approve Event
                    </button>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Active Deals / Promotions Section */}
          {(() => {
            const offers = (listing.schema_json as any)?.offers || [];
            const events = (listing.schema_json as any)?.events || [];
            const activeOffers = offers.filter((o: any) => o.status !== 'pending_approval');
            const activeEvents = events.filter((e: any) => e.status !== 'pending_approval');
            
            if (activeOffers.length === 0 && activeEvents.length === 0) return null;
            
            return (
              <Section title="Active Deals & Promotions" icon={<Check className="h-4 w-4 text-emerald-600" />}>
                <div className="space-y-3 pt-1.5">
                  {activeOffers.map((o: any) => (
                    <div key={o.id} className="bg-white border border-slate-200 p-3 rounded-xl flex items-center gap-3 shadow-sm">
                      {o.image && (
                        <img src={o.image} alt="" className="w-12 h-12 object-cover rounded-lg shrink-0 border border-slate-200 bg-slate-50" />
                      )}
                      <div className="flex-grow text-left">
                        <span className="text-[9px] bg-purple-100 text-purple-700 font-bold px-1.5 py-0.5 rounded uppercase">Offer</span>
                        <div className="text-xs font-bold text-slate-800 mt-1">{o.title}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{o.description}</div>
                      </div>
                    </div>
                  ))}

                  {activeEvents.map((e: any) => (
                    <div key={e.id} className="bg-white border border-slate-200 p-3 rounded-xl flex items-center gap-3 shadow-sm">
                      {e.image && (
                        <img src={e.image} alt="" className="w-12 h-12 object-cover rounded-lg shrink-0 border border-slate-200 bg-slate-50" />
                      )}
                      <div className="flex-grow text-left">
                        <span className="text-[9px] bg-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded uppercase">Event</span>
                        <div className="text-xs font-bold text-slate-800 mt-1">{e.title}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{e.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            );
          })()}

          {/* Engagement stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MetricPill icon={Eye} label="Views" value={listing.view_count} />
            <MetricPill icon={Phone} label="Calls" value={listing.call_clicks} />
            <MetricPill icon={MessageCircle} label="WhatsApp" value={listing.whatsapp_clicks} />
            <MetricPill icon={Navigation2} label="Directions" value={listing.direction_clicks} />
          </div>

          {/* Submitted Gallery */}
          {gallery.length > 0 && (
            <Section title="Submitted Photo Gallery" icon={<ImageIcon className="h-4 w-4 text-emerald-600" />}>
              <div className="grid grid-cols-5 gap-2 pt-1.5">
                {gallery.map((img, i) => (
                  <a
                    key={i}
                    href={img}
                    target="_blank"
                    rel="noreferrer"
                    className="aspect-square w-full rounded-lg overflow-hidden border border-slate-200 hover:border-emerald-600 hover:scale-105 transition-all shadow-sm bg-slate-50"
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </Section>
          )}

          {/* Contact Details */}
          <Section title="Listing Contact Information">
            <KV k="Phone" v={listing.phone} />
            <KV k="Email" v={listing.email} />
            <KV k="Website" v={listing.website} link />
            <KV k="Address" v={`${listing.address}, ${listing.city}, ${listing.state} ${listing.country}`} />
            {mapLink && <KV k="Google Map Directions" v={mapLink} link />}
            <KV k="Service Locations" v={registeredCities.join(', ')} />
          </Section>

          {/* Social Links */}
          {Object.values(social).some(Boolean) && (
            <Section title="Social Media Profiles" icon={<Globe className="h-4 w-4 text-emerald-600" />}>
              {social.facebook && <KV k="Facebook" v={social.facebook} link />}
              {social.instagram && <KV k="Instagram" v={social.instagram} link />}
              {social.twitter && <KV k="Twitter" v={social.twitter} link />}
              {social.linkedin && <KV k="LinkedIn" v={social.linkedin} link />}
            </Section>
          )}

          {/* YouTube Embed Video List */}
          {youtube.length > 0 && (
            <Section title="YouTube Videos Tagged" icon={<Video className="h-4 w-4 text-red-500" />}>
              <div className="space-y-1.5 pt-1">
                {youtube.map((url: string, i: number) => (
                  <KV key={i} k={`Video URL ${i + 1}`} v={url} link />
                ))}
              </div>
            </Section>
          )}

          <Section title="AI Summary" icon={<Sparkles className="h-4 w-4 text-emerald-600" />}>
            <p className="text-xs text-slate-600 leading-relaxed font-semibold">{listing.ai_summary ?? '—'}</p>
          </Section>

          <Section title="SEO Meta Parameters">
            <KV k="Meta title" v={seoTitle} />
            <KV k="Meta description" v={seoDesc} />
            <KV k="Slug" v={listing.slug} mono />
          </Section>

          <Section title="schema.org JSON-LD Context">
            <pre className="text-[10px] text-slate-600 font-mono bg-slate-50 border border-slate-200 rounded-xl p-3 overflow-x-auto scrollbar-thin">
{JSON.stringify(schema, null, 2)}
            </pre>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, icon }: { title: string; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{title}</div>
      </div>
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v, mono = false, link = false }: { k: string; v: string | null | undefined; mono?: boolean; link?: boolean }) {
  return (
    <div className="flex items-start gap-3 text-xs font-semibold text-slate-700">
      <div className="w-28 shrink-0 text-slate-400 text-[11px] pt-0.5">{k}</div>
      <div className={`flex-grow min-w-0 text-slate-800 ${mono ? 'font-mono text-[11px]' : ''} break-words`}>
        {link && v ? (
          <a href={v.startsWith('http') ? v : `https://${v}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-0.5 font-bold">
            {v} <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          v ?? '—'
        )}
      </div>
    </div>
  );
}

function MetricPill({ icon: Icon, label, value }: { icon: typeof Eye; label: string; value: number }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 shadow-sm text-center">
      <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 font-bold">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-base font-extrabold text-slate-850 mt-1 tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}
